import { getAdpApiHeaders } from './adpDataSources.api'
import { config } from '@/config'

// ─── Types ────────────────────────────────────────────────────────────────────

export type DynamicFieldOption = {
    label: string
    value: string
}

export type DynamicField = {
    type: 'input' | 'select' | 'multiselect' | 'textarea' | 'group' | 'code_editor' | 'radio'
    label: string
    key: string
    required?: boolean
    placeholder?: string
    dataSource?: string
    editable?: boolean
    options?: DynamicFieldOption[]
    repeatable?: boolean
    fields?: DynamicField[]
    visibleIf?: {
        field: string
        equals: string
    }
    requiredIf?: {
        field: string
        equals: string
    }
}

export type DynamicSchemaDefinition = {
    id: string
    label: string
    type: string
    apiKey: string
    version: string
    fields?: DynamicField[]
    transform?: {
        type: string
    }
}

export type TransformTypeDefinition = {
    id: number
    name: string
    description: string
    abbreviation: string
    min_no_of_columns: number
    schema_definition?: DynamicSchemaDefinition | null
}

type TransformationsApiResponse = {
    success: boolean
    status: number
    message?: string
    data?: TransformTypeDefinition[]
}

// ─── API function ─────────────────────────────────────────────────────────────

async function fetchTransformationsUrl(
    url: string
): Promise<TransformTypeDefinition[] | null> {
    const res = await fetch(url, {
        method: 'GET',
        headers: getAdpApiHeaders(),
    })

    if (!res.ok) {
        if (res.status === 404) {
            return null
        }
        const json = (await res.json().catch(() => ({}))) as TransformationsApiResponse
        throw new Error(json.message || `Transformations request failed (${res.status})`)
    }

    const json = (await res.json().catch(() => ({}))) as TransformationsApiResponse
    if (!json.success || !Array.isArray(json.data)) {
        throw new Error(json.message || 'Invalid transformations response')
    }

    // Filter out join and union (they belong to integrations)
    return json.data.filter(item =>
        item.name.toLowerCase() !== 'join' && item.name.toLowerCase() !== 'union'
    )
}

export async function fetchTransformations(
    baseUrl = config.adpApiUrl
): Promise<TransformTypeDefinition[]> {
    const normalizedBase = (typeof baseUrl === 'string' ? baseUrl.trim() : String(baseUrl)).replace(/\/+$/, '')
    if (!normalizedBase) {
        throw new Error('ADP API URL is not configured')
    }

    const candidates = [`${normalizedBase}/transformations`, `${normalizedBase}/records/transform_type`]

    let lastError: Error | null = null

    for (const url of candidates) {
        const result = await fetchTransformationsUrl(url)
        if (result !== null) {
            return result
        }
        lastError = new Error(`Transformations endpoint not found: ${url}`)
    }

    throw lastError ?? new Error('Transformations endpoint not found')
}

